import {
  ICreateSftpOutboundReportRequest,
  ICreateSftpPartnerRequest,
  IRunSftpOutboundReportRequest,
  IRunSftpOutboundReportResponse,
  ISetSftpOutboundReportEnabledRequest,
  ISetSftpPartnerEnabledRequest,
  ISftpPartnerDetailResponse,
  ISftpPartnerListResponse,
  IUpdateSftpOutboundReportRequest,
  IUpdateSftpPartnerRequest,
} from "types/SftpPartner";

import { api } from ".";

const partnerUrl = (slug: string) =>
  `/integration/sftp/partners/${encodeURIComponent(slug)}`;

const outboundReportsUrl = (slug: string) =>
  `${partnerUrl(slug)}/outbound-reports`;

const outboundReportUrl = (slug: string, id: string) =>
  `${outboundReportsUrl(slug)}/${encodeURIComponent(id)}`;

// Every write refetches the list (counts, status) and the partner's own page.
const partnerTags = (slug: string) => [
  { type: "SftpPartners" as const, id: "LIST" },
  { type: "SftpPartners" as const, id: slug },
];

// SFTP partners — SuperAdmin only (Integrations → SFTP).
export const sftpPartnerQueries = api.injectEndpoints({
  endpoints: (builder) => ({
    getSftpPartners: builder.query<ISftpPartnerListResponse, void>({
      query: () => ({
        url: "/integration/sftp/partners",
        method: "GET",
      }),
      providesTags: [{ type: "SftpPartners", id: "LIST" }],
    }),
    getSftpPartner: builder.query<ISftpPartnerDetailResponse, string>({
      query: (slug) => ({
        url: partnerUrl(slug),
        method: "GET",
      }),
      providesTags: (_result, _error, slug) => [
        { type: "SftpPartners", id: slug },
      ],
    }),
    // Enable / disable: the server accepts `enabled` and nothing else on PATCH.
    setSftpPartnerEnabled: builder.mutation<
      ISftpPartnerDetailResponse,
      ISetSftpPartnerEnabledRequest
    >({
      query: ({ slug, enabled }) => ({
        url: partnerUrl(slug),
        method: "PATCH",
        body: { enabled },
      }),
      invalidatesTags: (_result, _error, { slug }) => partnerTags(slug),
    }),
    // Create a partner (always PUSH, always disabled).
    createSftpPartner: builder.mutation<
      ISftpPartnerDetailResponse,
      ICreateSftpPartnerRequest
    >({
      query: (body) => ({
        url: "/integration/sftp/partners",
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { slug }) => partnerTags(slug),
    }),
    // Edit prefix, account, include tokens and mapping rules.
    updateSftpPartner: builder.mutation<
      ISftpPartnerDetailResponse,
      IUpdateSftpPartnerRequest
    >({
      query: ({ slug, body }) => ({
        url: partnerUrl(slug),
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _error, { slug }) => partnerTags(slug),
    }),
    // CLE-2864 — outgoing reports: files we write into the partner's folder. Created disabled.
    createSftpOutboundReport: builder.mutation<
      ISftpPartnerDetailResponse,
      ICreateSftpOutboundReportRequest
    >({
      query: ({ slug, body }) => ({
        url: outboundReportsUrl(slug),
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { slug }) => partnerTags(slug),
    }),
    // Edit account, folder and file name prefix (the type and partner are fixed).
    updateSftpOutboundReport: builder.mutation<
      ISftpPartnerDetailResponse,
      IUpdateSftpOutboundReportRequest
    >({
      query: ({ slug, id, body }) => ({
        url: outboundReportUrl(slug, id),
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _error, { slug }) => partnerTags(slug),
    }),
    setSftpOutboundReportEnabled: builder.mutation<
      ISftpPartnerDetailResponse,
      ISetSftpOutboundReportEnabledRequest
    >({
      query: ({ slug, id, enabled }) => ({
        url: outboundReportUrl(slug, id),
        method: "PATCH",
        body: { enabled },
      }),
      invalidatesTags: (_result, _error, { slug }) => partnerTags(slug),
    }),
    // Send now: write the file for a period of ended UTC days (replaces a file for the same period).
    runSftpOutboundReport: builder.mutation<
      IRunSftpOutboundReportResponse,
      IRunSftpOutboundReportRequest
    >({
      query: ({ slug, id, dateFrom, dateTo }) => ({
        url: `${outboundReportUrl(slug, id)}/run`,
        method: "POST",
        body: { dateFrom, dateTo },
      }),
      invalidatesTags: (_result, _error, { slug }) => partnerTags(slug),
    }),
  }),
});

export const {
  useGetSftpPartnersQuery,
  useGetSftpPartnerQuery,
  useSetSftpPartnerEnabledMutation,
  useCreateSftpPartnerMutation,
  useUpdateSftpPartnerMutation,
  useCreateSftpOutboundReportMutation,
  useUpdateSftpOutboundReportMutation,
  useSetSftpOutboundReportEnabledMutation,
  useRunSftpOutboundReportMutation,
} = sftpPartnerQueries;
